
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { isInstalledApp } from "@/lib/platform";
import { KeyRound, Mail, ArrowLeft } from "lucide-react";

/**
 * EmailSignIn — sends a magic link and also accepts the 6-digit code from the
 * same email.
 *
 * Why the code matters: inside an installed home-screen app the emailed link
 * opens in the system browser, which has a different storage jar, so the
 * session can never come back to the app. Typing the code completes sign-in
 * without ever leaving this webview.
 */
export function EmailSignIn() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const installed = isInstalledApp();

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const redirectTo = window.location.origin + window.location.pathname;

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo },
      });

      if (error) throw error;

      logger.debug("Sign-in email sent to:", email);
      setStep("code");

      toast({
        title: "Check your email",
        description: installed
          ? "Enter the 6-digit code from the email here to finish signing in."
          : "Open the link in the email, or enter the 6-digit code here.",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An unexpected error occurred";
      logger.error("Error sending sign-in email:", error);
      setError(message);
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code.trim(),
        type: "email",
      });
      if (error) throw error;
      // AuthModal / AuthProvider react to SIGNED_IN from here.
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "That code didn't work";
      logger.error("Error verifying code:", error);
      setError(message);
      toast({ title: "Invalid or expired code", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (step === "code") {
    return (
      <form onSubmit={handleVerifyCode} className="space-y-3">
        <div className="rounded-xl border bg-muted/40 p-3 text-xs text-muted-foreground flex items-start gap-2">
          <Mail className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
          <span>
            We emailed <strong className="text-foreground">{email}</strong>. Enter the 6-digit code
            below{installed ? " — this keeps you inside the app." : " or tap the link in the email."}
          </span>
        </div>

        {error && <p className="text-sm text-destructive font-medium">{error}</p>}

        <Input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={8}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-full text-center text-lg tracking-[0.4em] font-semibold"
          aria-label="6-digit sign-in code"
          required
        />

        <Button type="submit" disabled={loading || code.trim().length < 6} className="w-full">
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          {loading ? "Verifying…" : "Verify code & sign in"}
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={() => { setStep("email"); setCode(""); setError(null); }}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Use a different email
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSendLink} className="space-y-3">
      {error && <p className="text-sm text-destructive font-medium">{error}</p>}

      <Input
        type="email"
        id="email-sign-in"
        name="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="w-full"
        aria-label="Email address"
      />

      <Button type="submit" disabled={loading} className="w-full">
        <Mail className="h-4 w-4" aria-hidden="true" />
        {loading ? "Sending…" : "Continue with email"}
      </Button>

      <button
        type="button"
        onClick={() => { if (email) setStep("code"); }}
        className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
      >
        Already have a code? Enter it
      </button>
    </form>
  );
}
